import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  CustomerForEditData,
  CustomersListResponse,
  InviteCustomerRequest,
  InviteCustomerResponse,
  UpdateCustomerRequest,
  UpdateCustomerResponse,
} from "api-client";
import { CommonErrorHandlers } from "api-client";
import { OPERATOR_SCOPED_ID } from "../../constants/operator.constants";
import { customersService } from "../../services/customers.service";
import type { CustomerForEditPrefill, CustomersListState } from "./customers.models";

type CustomersListRootSlice = { customersList: CustomersListState };

/** API accepts a single `status=active` or `status=inactive`; both selected means no status filter. */
function resolveListStatusQuery(q: CustomersListState): string | undefined {
  const statuses = q.listFilterStatus.filter((s) => s === "active" || s === "inactive");
  if (statuses.length === 1) {
    return statuses[0];
  }
  if (q.showOnlyActive) {
    return "active";
  }
  return undefined;
}

// Use the centralized error handlers
const handleFetchError = CommonErrorHandlers.fetch("customers");
const handleInviteError = CommonErrorHandlers.create("customer invitation");
const handleUpdateError = CommonErrorHandlers.update("customer");

function parseStringIds(ids: string[]): number[] {
  return ids.map((id) => parseInt(id.trim(), 10)).filter((id) => Number.isFinite(id));
}

export const fetchCustomersList = createAsyncThunk<
  CustomersListResponse,
  void,
  { state: CustomersListRootSlice; rejectValue: string }
>("customersList/fetch", async (_, { getState, rejectWithValue }) => {
  const q = getState().customersList;
  try {
    return await customersService.getCustomers({
      search: q.search.trim() || undefined,
      locationIds:
        q.locationFilterIds.length > 0 ? parseStringIds(q.locationFilterIds) : undefined,
      status: resolveListStatusQuery(q),
      operatorIds: [OPERATOR_SCOPED_ID],
      fromIndex: q.fromIndex,
      toIndex: q.toIndex,
      sortingOrder: q.sortingOrder,
      sortingField: q.sortingField,
    });
  } catch (e: unknown) {
    return rejectWithValue(handleFetchError(e));
  }
});

export const inviteCustomer = createAsyncThunk<
  InviteCustomerResponse,
  InviteCustomerRequest,
  { state: CustomersListRootSlice; rejectValue: string }
>("customersList/inviteCustomer", async (body, { dispatch, rejectWithValue }) => {
  try {
    const res = await customersService.inviteCustomer(OPERATOR_SCOPED_ID, body);
    void dispatch(fetchCustomersList());
    return res;
  } catch (e: unknown) {
    return rejectWithValue(handleInviteError(e));
  }
});

export interface UpdateCustomerFromListArg {
  customerId: number;
  body: UpdateCustomerRequest;
}

function mapCustomerForEditToPrefill(data: CustomerForEditData): CustomerForEditPrefill {
  const locationOptions =
    data.locations.length > 0
      ? data.locations.map((loc) => ({
          value: String(loc.locationId),
          label: loc.locationName,
        }))
      : data.locationIds.map((id) => ({
          value: String(id),
          label: `Location ${id}`,
        }));

  const phoneFromWire =
    data.phoneNumber != null && String(data.phoneNumber).trim()
      ? String(data.phoneNumber).trim()
      : "";

  return {
    initialValues: {
      companyName: data.companyName,
      primaryContactName: data.primaryContactName,
      primaryContactEmail: data.emailId,
      phone: phoneFromWire,
      primaryBusinessAddress: data.customerAddress ?? "",
      locationIds: data.locationIds,
    },
    locationOptions,
  };
}

export const fetchCustomerForEdit = createAsyncThunk<
  CustomerForEditPrefill,
  number,
  { state: CustomersListRootSlice; rejectValue: string }
>("customersList/fetchCustomerForEdit", async (customerId, { rejectWithValue }) => {
  try {
    const response = await customersService.getCustomerForEdit(customerId, OPERATOR_SCOPED_ID);
    const data = response.data;
    if (!data?.customerId) {
      return rejectWithValue("Customer details were not returned.");
    }
    return mapCustomerForEditToPrefill(data);
  } catch (e: unknown) {
    return rejectWithValue(handleFetchError(e));
  }
});

export const updateCustomerFromList = createAsyncThunk<
  UpdateCustomerResponse,
  UpdateCustomerFromListArg,
  { state: CustomersListRootSlice; rejectValue: string }
>("customersList/updateCustomer", async (arg, { dispatch, rejectWithValue }) => {
  try {
    const res = await customersService.updateCustomer(arg.customerId, OPERATOR_SCOPED_ID, arg.body);
    void dispatch(fetchCustomersList());
    return res;
  } catch (e: unknown) {
    return rejectWithValue(handleUpdateError(e));
  }
});
